import { Col, Divider, Modal, Row, Typography } from "antd"
import { observer } from "mobx-react"
import { useTranslation } from "react-i18next"
import MainForm, { MainFormRef } from "../../../components/Form/MainForm"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { createRef, useEffect, useState } from "react"
import { FormField } from "../../../components/Form/FormField"
import { SellerServicesViewModel } from "../../sellersServices/SellerServicesViewModel"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import { ServiceStatus } from "../../../../domain/enum/serviceStatus.enum"
import { AssociatedService } from "../../../../domain/entities/Category"

type ServiceData = {
	id?: number
	weight: number
	title: string
	sellerName: string
}
export interface ServiceSessionModalProps {
	services: AssociatedService[]
	onConfirm?: (modalData: ServiceData) => void
	onCancel?: () => void
	visible: boolean
	editting?: boolean
}

const CategoryServiceModal = ({ services, visible, onConfirm, onCancel }: ServiceSessionModalProps) => {
	const { t } = useTranslation("microsites", { keyPrefix: "form.servicesInfo.serviceModal" })
	const sellerServicesViewModel = useContainerInjection<SellerServicesViewModel>("SellerServicesViewModel")
	const initialModalData: ServiceData = {
		id: undefined,
		weight: 0,
		title: "",
		sellerName: ""
	}
	const [modalData, setModalData] = useState<ServiceData>(initialModalData)
	const formRef = createRef<MainFormRef>()

	useEffect(() => {
		if (sellerServicesViewModel.sellersServices?.length === 0) {
			sellerServicesViewModel.fetchSellersServices({ limit: 2000 })
		}
		setModalData(initialModalData)
	}, [visible])

	return (
		<Modal
			title={
				<div style={{ position: "relative", bottom: 24 }}>
					<Typography.Title level={5}>{t("modalTitle")}</Typography.Title>
					<Divider style={{ marginBottom: 0 }} />
				</div>
			}
			centered
			style={{ overflowY: "auto", padding: "60px 0px" }}
			open={visible}
			onOk={async () => {
				await formRef.current?.validateFields()
				onConfirm &&
					onConfirm({
						...modalData,
						weight: Number(modalData.weight)
					})
			}}
			onCancel={() => onCancel && onCancel()}
			width={600}
			okText={t("confirm")}
			cancelText={t("cancel")}
		>
			<MainForm
				initialValues={modalData}
				hideFormButtons
				key={"categoryServiceModal"}
				onChangeFormValues={formData => {
					if (JSON.stringify(modalData) !== JSON.stringify(formData)) {
						setModalData({
							...modalData,
							...formData
						})
					}
				}}
				className="flex flex-col justify-center items-center"
				ref={formRef}
			>
				<Row gutter={[24, 0]} className="w-full">
					<Col md={24} xl={24} xs={24}>
						<Row gutter={[24, 0]}>
							<Col xl={24} md={24} xs={24} className="md:mx-auto">
								<FormField.SelectSearch
									key={"id"}
									label={t("service")}
									name={"id"}
									asyncFetchSearchFunction={async searchValue =>
										await sellerServicesViewModel.searchServices(searchValue)
									}
									mandatory
									value={modalData?.id}
									placeholder={t("servicePlaceholder").toString()}
									onChange={serviceSelected => {
										const service = sellerServicesViewModel.sellersServices.find(
											s => s.id === serviceSelected.value
										)
										setModalData({
											...modalData,
											id: serviceSelected?.value,
											title: service?.title ?? "",
											sellerName: service?.name ?? ""
										})
									}}
									options={sellerServicesViewModel.sellersServices
										.filter(
											sellerService =>
												sellerService.status === ServiceStatus.ACTIVE &&
												services?.every(service => {
													return service.id !== sellerService.id
												})
										)
										.map(service => ({
											label: `${service.id} - ${service.title}` || "",
											value: service.id,
											title: service.title
										}))}
								/>
							</Col>
						</Row>
						<Row gutter={[24, 0]}>
							<Col xl={24} md={24} xs={24} className=" md:ms-auto">
								<FormField.Input
									inputType={InputType.NUMBER}
									key={"weight"}
									label={t("weight")}
									name={"weight"}
									mandatory
								/>
							</Col>
						</Row>
					</Col>
				</Row>
			</MainForm>
		</Modal>
	)
}

export default observer(CategoryServiceModal)
